import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  convertToCurrencyFormat,
  getSerialFromBarcode,
  stockUnitOfMeasureOptions,
  isReadOnlyUser,
} from "../../shared/utility";
import AuditActionLink from "../../components/audit/AuditActionLink";
import NumberInputText from "../../components/controls/inputs/NumberInputText";
import { useQueuedRowEdits } from "../../hooks/useQueuedRowEdits";

export default function CreditReturnCartonsTable(props) {
  const { askDeleteCarton, values, updateCreditReturnItem } = props;
  const isReadOnly = isReadOnlyUser();

  const withUpdatedTotalCost = (item) => {
    let quantity = item.piece_count;
    if (item.stock_unit_of_measurement === 1) quantity = item.weight_in_kg;
    if (item.stock_unit_of_measurement === 2) quantity = item.weight_in_lbs;

    return {
      ...item,
      total_cost: (Number(quantity) || 0) * (Number(item.unit_cost) || 0),
    };
  };

  const {
    rows: items,
    getFieldSaveStatus,
    saveField,
  } = useQueuedRowEdits({
    rows: values.items ?? [],
    keyPrefix: "credit-return",
    prepareRow: withUpdatedTotalCost,
    onSave: (item) =>
      updateCreditReturnItem(item.id, {
        unit_cost: item.unit_cost,
        total_cost: item.total_cost,
      }),
  });

  function bodyActions(rowData) {
    return (
      <div className="flex items-center">
        {rowData.deleted_at ? (
          <p className="text-black font-bold text-lg">DELETED</p>
        ) : (
          !isReadOnly && (
            <button
              onClick={() => {
                askDeleteCarton(rowData.id);
              }}
              className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
            >
              Delete
            </button>
          )
        )}
        {rowData.id ? (
          <AuditActionLink
            slug="credit-return-item"
            id={rowData.id}
            name={rowData.product_name || rowData.lot_number}
          />
        ) : null}
      </div>
    );
  }

  const productDetailsBody = (rowData) => {
    return (
      <p>
        <span className="text-gray-900 font-semibold">
          {rowData.product_name}
        </span>
        <br />
        <span className="text-gray-700">PLU:{rowData.plu}</span>
      </p>
    );
  };

  const unitCostBody = (rowData) => {
    const stockUofM = stockUnitOfMeasureOptions.find(
      (opt) => opt.value === rowData.stock_unit_of_measurement,
    )?.label;
    return (
      <div className="flex items-center justify-end gap-1">
        <NumberInputText
          value={rowData.unit_cost}
          aria-label={`Unit cost for ${rowData.product_name}`}
          inputClassName="editable-table-input"
          className="editable-table-number"
          disabled={isReadOnly || Boolean(rowData.deleted_at)}
          saveStatus={getFieldSaveStatus(rowData, "unit_cost")}
          onCommit={(unitCost) => saveUnitCost(rowData, unitCost)}
          mode="currency"
          currency="USD"
          locale="en-US"
          minFractionDigits={2}
          maxFractionDigits={2}
          min={0}
        />
        <span>/{stockUofM}</span>
      </div>
    );
  };

  const totalCostBody = (rowData) => {
    var totalCost = 4;

    if (rowData.stock_unit_of_measurement === 1) {
      totalCost =
        parseFloat(rowData.weight_in_kg) * parseFloat(rowData.unit_cost);
    } else if (rowData.stock_unit_of_measurement === 2) {
      totalCost =
        parseFloat(rowData.weight_in_lbs) * parseFloat(rowData.unit_cost);
    } else {
      totalCost =
        parseFloat(rowData.piece_count) * parseFloat(rowData.unit_cost);
    }

    return <p>{convertToCurrencyFormat(totalCost)}</p>;
  };

  const totalShippedWeightParent = (rowData) => {
    return (
      <div>
        <p>{rowData.weight_in_kg}kg</p>
        <p>{rowData.weight_in_lbs}lbs</p>
        <p>{rowData.piece_count} pieces</p>
      </div>
    );
  };

  const barcodeBody = (rowData) => {
    return <p>{getSerialFromBarcode(rowData.barcode)}</p>;
  };

  const saveUnitCost = (item, unitCost) => {
    return saveField(item, "unit_cost", unitCost);
  };

  return (
    <div className="">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div className="overflow-hidden">
            <DataTable
              value={items}
              dataKey="id"
              tableClassName="min-w-full bg-white divide-y divide-gray-300 cr-items-table-parent"
              cellClassName="p-2 bg-white border-1-bottom-gray text-sm"
            >
              <Column header="Product" body={productDetailsBody} />
              <Column header="Barcode" body={barcodeBody} />
              <Column header="Lot Number" field="lot_number" />

              <Column
                header="Amount"
                body={totalShippedWeightParent}
                style={{ textAlign: "right" }}
              />

              <Column
                header="Unit Cost"
                field="unit_cost"
                style={{ textAlign: "right" }}
                body={unitCostBody}
              />

              <Column
                header="Total Cost"
                field="total_cost"
                style={{ textAlign: "right" }}
                body={totalCostBody}
              />

              <Column header="" body={bodyActions} />
            </DataTable>
          </div>

          {/* <div className="w-full text-right mt-4">
            <p className="font-bold">
              Subtotal: {convertToCurrencyFormat (values.subtotal_dollar_amount)}
            </p>
            <p className="font-bold">
              Taxes: {convertToCurrencyFormat(values.tax_amount)}
            </p>
            <p className="font-bold">
              Total: {convertToCurrencyFormat(values.total_dollar_amount)}
            </p>
          </div> */}
        </div>
      </div>
    </div>
  );
}
